import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Header from '@/components/base/Header';
import Footer from '@/components/base/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, animeList } from '@/mocks/anime';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const category = useMemo(() => {
    return categories.find((c) => c.slug === slug);
  }, [slug]);

  const filteredAnime = useMemo(() => {
    if (!category) return [];
    return animeList
      .filter((a) => a.categories.includes(category.name))
      .sort((a, b) => {
        if (sortBy === 'latest') return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
        if (sortBy === 'popular') return b.views - a.views;
        return b.rating - a.rating;
      });
  }, [category, sortBy]);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Header />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-16 text-center">
          <div className="w-20 h-20 mx-auto bg-background-100 rounded-full flex items-center justify-center mb-4">
            <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-950 mb-2">分类不存在</h1>
          <p className="text-sm text-foreground-500 mb-6">找不到该分类，请返回首页浏览其他内容。</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name} - 51动漫网 | 免费好看的${category.name}在线阅读`;
  const pageDescription = `51动漫网${category.name}专区，汇集${category.count}+部热门${category.name}作品，提供高清免费在线阅读。最新${category.name}连载、完结${category.name}一网打尽。`;

  return (
    <>
      <ScrollToTop />
      <Header />
      <Breadcrumb />
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 pb-16">
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        <div className="pt-4 mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
              <i className={`${category.icon} text-accent-600 text-lg`}></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
              {category.name}
            </h1>
          </div>
          <p className="text-sm text-foreground-500 ml-13">
            共 {category.count}+ 部作品 · 51动漫网
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 shrink-0">
            {(['latest', 'popular', 'rating'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  sortBy === s
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {s === 'latest' ? '最新' : s === 'popular' ? '最热' : '评分'}
              </button>
            ))}
          </div>
        </div>

        {filteredAnime.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-foreground-500">暂无该分类的动漫作品</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredAnime.map((anime) => (
              <Link
                key={anime.id}
                to={`/article/${anime.slug}`}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-xl aspect-[3/4] mb-3">
                  <img
                    src={anime.cover}
                    alt={anime.title}
                    title={`${anime.title} - 51动漫网`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2 left-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                      anime.status === '连载中'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-foreground-600 text-background-50'
                    }`}>
                      {anime.status}
                    </span>
                  </div>
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-background-950/70 backdrop-blur-sm rounded-full px-2 py-0.5">
                    <i className="ri-star-fill text-accent-500 text-xs"></i>
                    <span className="text-xs text-background-50 font-medium">{anime.rating}</span>
                  </div>
                </div>
                <h2 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                  {anime.title}
                </h2>
                <p className="text-xs text-foreground-400 mt-1">{anime.updateChapter}</p>
              </Link>
            ))}
          </div>
        )}

        {filteredAnime.length > 0 && (
          <div className="text-center mt-10">
            <p className="text-sm text-foreground-400">
              共找到 <strong className="text-foreground-700">{filteredAnime.length}</strong> 部{category.name}作品
            </p>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}